import { type ReactNode, useEffect, useRef, useState } from 'react'

import { CaretDownIcon, CaretUpIcon, Mtds } from '../../index'
import { type HistoryTimelineItem } from '../../types/types'
import { historyPresentationTitle } from '../../utils/historyPresentation'
import { toLocaleTimeString } from '../../utils/dateUtils'
import {
  getProcessingErrorCopy,
  normalizeProcessingErrorText,
  shouldHideProcessingErrorDetails
} from '../../utils/processingErrorUtils'
import {
  formatSampleFieldValue,
  getSampleFieldLabel
} from '../../utils/sampleFieldDisplayUtils'

type HistoryTimelineCardProps = {
  item: HistoryTimelineItem
  isOpen: boolean
  onToggle: (item: HistoryTimelineItem) => void
  locale?: 'nb' | 'en'
}

export const HistoryTimelineCard = ({
  item,
  isOpen,
  onToggle,
  locale = 'nb'
}: HistoryTimelineCardProps) => {
  const changes = item.changes ?? []
  const occurredAt = toLocaleTimeString(item.occurredAt)
  const actorName = item.actor?.name ?? item.actor?.id
  const metadata = [occurredAt, actorName].filter(Boolean).join(' · ')
  const processingErrorCopy = getProcessingErrorCopy(locale)
  const description =
    item.kind === 'ResultConclusionReset' ? undefined : item.description
  const isProcessingFailure = shouldHideProcessingErrorDetails(item)

  return (
    <Mtds.Grid className='mb-8 min-w-0 w-full flex-1 pt-2.5'>
      <Mtds.Grid data-gap={1}>
        <Mtds.Flex data-align='center' data-gap={1}>
          <strong>{historyPresentationTitle(item)}</strong>
        </Mtds.Flex>
        {description &&
          (isProcessingFailure ? (
            <>
              <p>{processingErrorCopy.explanation}</p>
              <p>{normalizeProcessingErrorText(description, locale)}</p>
            </>
          ) : (
            <p>{description}</p>
          ))}
      </Mtds.Grid>

      {changes.length > 0 && (
        <HistoryExpandableContent
          ariaLabel={isOpen ? 'Skjul endringer' : 'Vis endringer'}
          isOpen={isOpen}
          onToggle={() => onToggle(item)}
        >
          <Mtds.Grid data-gap='3'>
            {changes.map((change) => (
              <Mtds.Flex
                key={change.field}
                data-gap='1'
                className='flex-wrap min-w-0'
              >
                <HistoryChangeSummary change={change} />
              </Mtds.Flex>
            ))}
          </Mtds.Grid>
        </HistoryExpandableContent>
      )}
      {metadata && <Mtds.Muted>{metadata}</Mtds.Muted>}
    </Mtds.Grid>
  )
}

export const hasExpandableHistoryDetails = (item: HistoryTimelineItem) =>
  (item.changes?.length ?? 0) > 0

type HistoryChange = NonNullable<HistoryTimelineItem['changes']>[number]

const HistoryExpandableContent = ({
  ariaLabel,
  children,
  isOpen,
  onToggle
}: {
  ariaLabel: string
  children: ReactNode
  isOpen: boolean
  onToggle: () => void
}) => {
  const [hasOverflow, setHasOverflow] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const content = contentRef.current
    if (!content) return
    const measure = () => setHasOverflow(content.scrollHeight > 97)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    return () => observer.disconnect()
  }, [children])

  return (
    <Mtds.Card data-pad={3} className='relative min-w-0 w-full'>
      {hasOverflow && (
        <Mtds.Button
          type='button'
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          className='absolute right-2 top-2 z-10 bg-transparent! p-0! hover:bg-transparent!'
          data-size='sm'
          data-variant='tertiary'
          onClick={onToggle}
        >
          {isOpen ? <CaretUpIcon /> : <CaretDownIcon />}
        </Mtds.Button>
      )}
      <div
        ref={contentRef}
        className={`min-w-0 font-normal! ${hasOverflow ? 'pr-6' : ''} ${!isOpen && hasOverflow ? 'max-h-24 overflow-hidden [mask-image:linear-gradient(to_bottom,currentColor_calc(100%_-_var(--mtds-4)),transparent)]' : ''}`}
      >
        {children}
      </div>
    </Mtds.Card>
  )
}

const HistoryChangeSummary = ({ change }: { change: HistoryChange }) => {
  const fromValue = formatSampleFieldValue(change.field, change.from)
  const toValue = formatSampleFieldValue(change.field, change.to)
  const hasStructuredValues =
    isExpandableHistoryValue(fromValue) || isExpandableHistoryValue(toValue)

  if (hasStructuredValues) {
    return (
      <Mtds.Grid data-gap={1} className='min-w-0 w-full'>
        <Mtds.Flex data-gap={1} data-align='center' className='min-w-0'>
          <Mtds.Tag data-icon='none' className='shrink-0'>
            {getSampleFieldLabel(change.field)}
          </Mtds.Tag>
          <Mtds.Muted aria-hidden='true' className='shrink-0'>
            :
          </Mtds.Muted>
        </Mtds.Flex>
        <div className='grid min-w-0 grid-cols-1 items-start gap-2 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]'>
          <HistoryChangeValue data-color='danger' fill value={fromValue} />
          <Mtds.Muted
            aria-label='endret til'
            className='justify-self-center self-start'
          >
            →
          </Mtds.Muted>
          <HistoryChangeValue data-color='success' fill value={toValue} />
        </div>
      </Mtds.Grid>
    )
  }

  return (
    <Mtds.Flex
      data-gap={1}
      data-align='center'
      className='min-w-0 w-full flex-wrap'
    >
      <Mtds.Tag data-icon='none' className='shrink-0'>
        {getSampleFieldLabel(change.field)}
      </Mtds.Tag>
      <Mtds.Muted aria-hidden='true' className='shrink-0'>
        :
      </Mtds.Muted>
      <HistoryChangeValue data-color='danger' value={fromValue} />
      <span className='inline-flex min-w-0 items-center gap-1'>
        <Mtds.Muted aria-label='endret til' className='shrink-0'>
          →
        </Mtds.Muted>
        <HistoryChangeValue data-color='success' value={toValue} />
      </span>
    </Mtds.Flex>
  )
}

const LONG_VALUE_LENGTH = 160
const LONG_VALUE_LINE_BREAKS = 3

const isExpandableHistoryValue = (value: string) => {
  return (
    value.length > LONG_VALUE_LENGTH ||
    value.split('\n').length > LONG_VALUE_LINE_BREAKS
  )
}

const HistoryChangeValue = ({
  'data-color': dataColor,
  fill = false,
  value
}: {
  'data-color': 'danger' | 'success'
  fill?: boolean
  value: string
}) => {
  return (
    <Mtds.Tag
      data-color={dataColor}
      data-icon='none'
      className={`${fill ? 'w-full' : ''} min-w-0 max-w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere]`}
    >
      {value}
    </Mtds.Tag>
  )
}
